<template>
    <div>
        <div class="page-header pr-0">
            <h2><a href="#"><i class="fas fa-utensils"></i></a></h2>
            <ol class="breadcrumbs">
                <li class="active"><span>Restaurante V2</span></li>
                <li><span>Mapa de mesas</span></li>
            </ol>
        </div>

        <div class="card mb-0 restaurant-map-card">
            <div class="card-body">
                <div class="restaurant-map-header">
                    <h4>Mapa de mesas</h4>

                    <el-button type="primary" icon="el-icon-refresh" @click="getRecords">
                        Actualizar
                    </el-button>

                    <el-button type="warning" icon="el-icon-date" @click="openReservationsDialog">
                        Reservas
                    </el-button>
                </div>

                <el-tabs v-model="active_room" class="restaurant-room-tabs">
                    <el-tab-pane v-for="room in rooms" :key="room.id" :label="room.name" :name="String(room.id)">

                        <div class="restaurant-room-panel">
                            <div class="restaurant-room-title">
                                <div>
                                    <i class="fas fa-chair"></i>
                                    <strong>{{ room.name }}</strong>
                                </div>

                                <div class="restaurant-color-legend">
                                    <el-tooltip content="Libre" placement="top">
                                        <span class="legend-square free"></span>
                                    </el-tooltip>

                                    <el-tooltip content="Pre cuenta solicitada" placement="top">
                                        <span class="legend-square ready"></span>
                                    </el-tooltip>

                                    <el-tooltip content="Ocupada" placement="top">
                                        <span class="legend-square occupied"></span>
                                    </el-tooltip>

                                    <el-tooltip content="Reservada" placement="top">
                                        <span class="legend-square reserved"></span>
                                    </el-tooltip>
                                </div>

                                <div class="restaurant-room-stats">
                                    <span class="stat-item">
                                        <i class="fas fa-chair"></i>
                                        {{ room.tables.length }} mesas
                                    </span>

                                    <span class="stat-item">
                                        <span class="stat-square free"></span>
                                        {{ groupedTables(room).available.length }} libres
                                    </span>

                                    <span class="stat-item">
                                        <span class="stat-square occupied"></span>
                                        {{ groupedTables(room).occupied.length }} ocupadas
                                    </span>

                                    <span class="stat-item">
                                        <span class="stat-square reserved"></span>
                                        {{ groupedTables(room).reserved.length }} reservadas
                                    </span>

                                    <span class="stat-item">
                                        <span class="stat-square ready"></span>
                                        {{ groupedTables(room).ready_to_pay.length }} pre cuenta
                                    </span>
                                </div>
                            </div>

                            <template v-if="room.tables.length > 0">
                                <div v-if="groupedTables(room).available.length" class="restaurant-table-section">
                                    <h5 class="section-free">Mesas libres</h5>

                                    <div class="restaurant-table-grid">
                                        <div v-for="table in groupedTables(room).available" :key="table.id"
                                            class="restaurant-table-tile available" @click="openTable(table)">

                                            <div class="restaurant-table-number">
                                                {{ table.name }}
                                            </div>

                                            <div class="restaurant-table-capacity">
                                                {{ table.capacity }} asientos
                                            </div>

                                            <button v-if="allowReservations" class="table-mini-action reserve"
                                                @click.stop="reserveTable(table)">
                                                Reservar
                                            </button>

                                            <i class="fas fa-chair restaurant-table-icon"></i>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="groupedTables(room).occupied.length" class="restaurant-table-section">
                                    <h5 class="section-occupied">Mesas ocupadas</h5>

                                    <div class="restaurant-table-grid">
                                        <div v-for="table in groupedTables(room).occupied" :key="table.id"
                                            class="restaurant-table-tile occupied" @click="goOrder(table.order_id)">

                                            <div class="restaurant-table-number">
                                                {{ table.name }}
                                            </div>

                                            <div class="restaurant-table-capacity">
                                                {{ table.people_quantity || table.capacity }} asientos
                                            </div>

                                            <div v-if="showElapsedTime" class="restaurant-table-time">
                                                <i class="far fa-clock"></i>
                                                {{ formatOccupiedTime(table.occupied_minutes) }}
                                            </div>

                                            <i class="fas fa-chair restaurant-table-icon"></i>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="groupedTables(room).ready_to_pay.length" class="restaurant-table-section">
                                    <h5 class="section-ready">Mesas con pre cuenta</h5>

                                    <div class="restaurant-table-grid">
                                        <div v-for="table in groupedTables(room).ready_to_pay" :key="table.id"
                                            class="restaurant-table-tile ready-to-pay" @click="goOrder(table.order_id)">

                                            <div class="restaurant-table-number">
                                                {{ table.name }}
                                            </div>

                                            <div class="restaurant-table-capacity">
                                                {{ table.people_quantity || table.capacity }} asientos
                                            </div>

                                            <div class="restaurant-table-time ready-label">
                                                <i class="fas fa-file-invoice"></i>
                                                Pre cuenta
                                            </div>

                                            <i class="fas fa-chair restaurant-table-icon"></i>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="groupedTables(room).reserved.length" class="restaurant-table-section">
                                    <h5 class="section-reserved">Mesas reservadas</h5>

                                    <div class="restaurant-table-grid">
                                        <div v-for="table in groupedTables(room).reserved" :key="table.id"
                                            class="restaurant-table-tile reserved"
                                            :class="{ 'reservation-soon': isReservationSoon(table) }"
                                            @click="showReservationDetail(table)">

                                            <div class="restaurant-table-number">
                                                {{ table.name }}
                                            </div>

                                            <div class="restaurant-table-capacity">
                                                {{ table.capacity }} asientos
                                            </div>

                                            <div class="restaurant-table-time reserved-label">
                                                <i class="far fa-clock"></i>
                                                {{ formatReservationHour(table.reserved_at) }}
                                            </div>

                                            <div v-if="isReservationSoon(table)"
                                                class="restaurant-reservation-soon-badge">
                                                Próxima
                                            </div>

                                            <div class="restaurant-reserved-customer">
                                                {{ table.reserved_customer_name || 'Sin cliente' }}
                                                <small v-if="table.reserved_customer_number">
                                                    {{ table.reserved_customer_number }}
                                                </small>
                                            </div>

                                            <div class="table-reserved-actions" style="padding-bottom: 20px;">
                                                <button @click.stop="openTable(table)">
                                                    Abrir
                                                </button>

                                                <button class="release" @click.stop="releaseTable(table)">
                                                    Liberar
                                                </button>
                                            </div>

                                            <i class="fas fa-chair restaurant-table-icon"></i>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div class="alert alert-info mb-0" v-else>
                                Este salón no tiene mesas activas.
                            </div>
                        </div>
                    </el-tab-pane>
                </el-tabs>

                <el-dialog :title="editingReservation ? 'Editar reserva' : 'Reservar mesa'"
                    :visible.sync="showReserveDialog" width="650px">

                    <div v-if="reserveTableSelected" class="restaurant-reserve-header">
                        <div>
                            <span>Mesa</span>
                            <strong>{{ reserveTableSelected.name }}</strong>
                        </div>
                        <div>
                            <span>Cliente de reserva</span>
                            <strong>{{ reserveForm.customer_name || 'Sin cliente' }}</strong>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>
                            Cliente
                            <a href="#" class="ml-1" @click.prevent="clickCreateCustomer">[+
                                Nuevo]</a>
                        </label>

                        <el-select v-model="reserveForm.customer_id" filterable remote clearable class="w-100"
                            popper-class="el-select-customers"
                            placeholder="Escriba nombre o número de documento del cliente"
                            :remote-method="searchRemoteCustomers" :loading="loadingSearchCustomer"
                            @focus="loadReserveCustomers" @visible-change="visibleReserveCustomers"
                            @change="changeReserveCustomer">
                            <el-option v-for="option in reserveCustomers" :key="option.id" :label="option.description"
                                :value="option.id">
                            </el-option>
                        </el-select>
                    </div>

                    <div class="form-group">
                        <label>Fecha y hora de reserva</label>
                        <el-date-picker v-model="reserveForm.reserved_at" type="datetime" class="w-100"
                            format="dd/MM/yyyy HH:mm" value-format="yyyy-MM-dd HH:mm:ss"
                            placeholder="Seleccione fecha y hora">
                        </el-date-picker>
                    </div>

                    <div class="form-group">
                        <label>Observación</label>
                        <el-input type="textarea" rows="3" v-model="reserveForm.reserved_observation"
                            placeholder="Ejemplo: llega 8:30 PM, mesa cerca a ventana">
                        </el-input>
                    </div>

                    <span slot="footer">
                        <el-button @click="showReserveDialog = false">Cancelar</el-button>
                        <el-button type="primary" :loading="loadingReserve" @click="submitReserveTable">
                            {{ editingReservation ? 'Actualizar' : 'Reservar' }}
                        </el-button>
                    </span>
                </el-dialog>

                <el-dialog title="Datos de reserva" :visible.sync="showReservationDetailDialog" width="520px">
                    <div v-if="reservationDetail" class="restaurant-reservation-detail">
                        <div class="reservation-detail-row">
                            <span>Mesa</span>
                            <strong>{{ reservationDetail.name }}</strong>
                        </div>

                        <div class="reservation-detail-row">
                            <span>Capacidad</span>
                            <strong>{{ reservationDetail.capacity }} asientos</strong>
                        </div>

                        <div class="reservation-detail-row">
                            <span>Cliente</span>
                            <strong>{{ reservationDetail.reserved_customer_name || 'Sin cliente'
                                }}</strong>
                        </div>

                        <div class="reservation-detail-row">
                            <span>DNI/RUC</span>
                            <strong>{{ reservationDetail.reserved_customer_number || '-' }}</strong>
                        </div>

                        <div class="reservation-detail-row">
                            <span>Fecha y hora de reserva</span>
                            <strong>{{ formatReservationDate(reservationDetail.reserved_at)
                                }}</strong>
                        </div>

                        <div class="reservation-detail-row">
                            <span>Observaciones</span>
                            <strong>{{ reservationDetail.reserved_observation || '-' }}</strong>
                        </div>
                    </div>

                    <span slot="footer">
                        <el-button @click="showReservationDetailDialog = false">Cerrar</el-button>
                        <el-button type="primary" @click="openReservedFromDetail">
                            Abrir mesa
                        </el-button>
                    </span>
                </el-dialog>

                <el-dialog title="Reservas" :visible.sync="showReservationsDialog" width="720px">
                    <div v-loading="loadingReservations">
                        <div class="restaurant-reservation-filters">
                            <el-date-picker v-model="reservationFilters.date" type="date" format="dd/MM/yyyy"
                                value-format="yyyy-MM-dd" placeholder="Fecha">
                            </el-date-picker>

                            <el-select v-model="reservationFilters.room_name" clearable placeholder="Salón">
                                <el-option v-for="room in reservationRooms" :key="room" :label="room" :value="room">
                                </el-option>
                            </el-select>

                            <el-input v-model="reservationFilters.customer" clearable placeholder="Cliente">
                            </el-input>
                        </div>
                        <div v-for="row in filteredReservations" :key="row.id" class="restaurant-reservation-list-row">
                            <div>
                                <strong>{{ row.room_name }} - {{ row.table_name }}</strong>
                                <span>{{ row.reserved_customer_number || '-' }} - {{ row.reserved_customer_name
                                }}</span>
                                <small>{{ formatReservationDate(row.reserved_at) }}</small>
                                <p v-if="row.reserved_observation">{{ row.reserved_observation }}</p>
                            </div>
                            <div class="restaurant-reservation-list-actions">
                                <el-button size="mini" type="success" @click="openReservationFromList(row)">
                                    Abrir mesa
                                </el-button>

                                <el-button size="mini" type="primary" @click="editReservation(row)">
                                    Editar
                                </el-button>

                                <el-button size="mini" type="danger" @click="requestReleaseReservation(row)">
                                    Liberar
                                </el-button>
                            </div>
                        </div>

                        <div v-if="filteredReservations.length === 0" class="alert alert-info mb-0">
                            No hay reservas registradas.
                        </div>
                    </div>
                </el-dialog>

                <el-dialog title="Código de autorización" :visible.sync="showReservationCodeDialog" width="420px"
                    :close-on-click-modal="false">
                    <div class="form-group">
                        <label>Ingrese el código para continuar</label>
                        <el-input v-model="reservationCodeForm.code" show-password placeholder="Código"
                            @keyup.enter.native="confirmReservationCode">
                        </el-input>
                    </div>

                    <span slot="footer">
                        <el-button @click="closeReservationCodeDialog">
                            Cancelar
                        </el-button>

                        <el-button type="primary" @click="confirmReservationCode">
                            Confirmar
                        </el-button>
                    </span>
                </el-dialog>

                <tenant-person-form :showDialog.sync="showDialogNewPerson" type="customers" :external="true"
                    :input_person="input_person" @success="successAddPerson">
                </tenant-person-form>

                <div class="alert alert-info mb-0" v-if="rooms.length === 0">
                    Registra salones y mesas activas para usar el mapa.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            resource: 'restaurant/v2/tables-map',
            rooms: [],
            active_room: null,
            refreshInterval: null,
            restaurant_configuration: {
                restaurant_allow_reservations: true,
                restaurant_show_elapsed_time: true,
                restaurant_use_pre_account_color: true,
                restaurant_allow_charge_without_pre_account: true,
            },
            showReserveDialog: false,
            reserveTableSelected: null,
            reserveCustomers: [],
            loadingReserve: false,
            loadingSearchCustomer: false,
            showDialogNewPerson: false,
            input_person: {},
            reserveForm: {
                customer_id: null,
                customer_name: null,
                customer_number: null,
                reserved_observation: null,
                reserved_at: null,
            },
            showReservationDetailDialog: false,
            reservationDetail: null,
            showReservationsDialog: false,
            reservations: [],
            loadingReservations: false,
            editingReservation: false,
            showReservationCodeDialog: false,
            reservationCodeForm: {
                action: null,
                code: null,
                row: null,
            },
            reservationFilters: {
                date: null,
                room_name: null,
                customer: null,
            },
        }
    },
    created() {
        this.getRecords()
    },
    mounted() {
        this.refreshInterval = setInterval(() => {
            this.getRecords()
        }, 60000)
    },
    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval)
        }
    },
    computed: {
        allowReservations() {
            return this.restaurant_configuration.restaurant_allow_reservations === true
        },

        showElapsedTime() {
            return this.restaurant_configuration.restaurant_show_elapsed_time === true
        },

        usePreAccountColor() {
            return this.restaurant_configuration.restaurant_use_pre_account_color === true
                && this.restaurant_configuration.restaurant_allow_charge_without_pre_account !== true
        },
        reservationRooms() {
            return [...new Set(this.reservations.map(row => row.room_name).filter(row => row))]
        },

        filteredReservations() {
            return this.reservations.filter(row => {
                const dateMatch = !this.reservationFilters.date
                    || moment(row.reserved_at).format('YYYY-MM-DD') === this.reservationFilters.date

                const roomMatch = !this.reservationFilters.room_name
                    || row.room_name === this.reservationFilters.room_name

                const customerInput = (this.reservationFilters.customer || '').toLowerCase()
                const customerText = `${row.reserved_customer_number || ''} ${row.reserved_customer_name || ''}`.toLowerCase()

                const customerMatch = !customerInput || customerText.includes(customerInput)

                return dateMatch && roomMatch && customerMatch
            })
        },
    },
    methods: {
        getRecords() {
            this.$http.get(`/${this.resource}/records`).then(response => {
                this.rooms = response.data.data || []

                this.restaurant_configuration = Object.assign(
                    {},
                    this.restaurant_configuration,
                    response.data.restaurant_configuration || {}
                )

                if (this.rooms.length > 0 && !this.active_room) {
                    this.$nextTick(() => {
                        this.active_room = String(this.rooms[0].id)
                    })
                }
            }).catch(error => {
                console.log(error)
                this.$message.error('No se pudo cargar el mapa de mesas.')
            })
        },
        groupedTables(room) {
            return {
                available: room.tables.filter(table => table.status === 'available'),
                occupied: room.tables.filter(table => {
                    return table.status === 'occupied' || (!this.usePreAccountColor && table.status === 'ready_to_pay')
                }),
                reserved: room.tables.filter(table => table.status === 'reserved'),
                ready_to_pay: this.usePreAccountColor
                    ? room.tables.filter(table => table.status === 'ready_to_pay')
                    : [],
                maintenance: room.tables.filter(table => table.status === 'maintenance'),
            }
        },
        openTable(table) {
            if (!['available', 'reserved'].includes(table.status)) {
                return
            }

            const message = table.status === 'reserved'
                ? `La mesa ${table.name} está reservada. ¿Deseas abrirla ahora?`
                : `¿Abrir la mesa ${table.name}?`

            this.$confirm(message, 'Abrir mesa', {
                confirmButtonText: 'Abrir',
                cancelButtonText: 'Cancelar',
                type: 'info',
            }).then(() => {
                this.$http.post(`/${this.resource}/open`, {
                    restaurant_dining_table_id: table.id,
                    people_quantity: table.people_quantity || table.capacity || 1,
                }).then(response => {
                    if (response.data.success) {
                        this.$message.success(response.data.message)
                        this.goOrder(response.data.order_id)
                    } else {
                        this.$message.warning(response.data.message)
                        if (response.data.order_id) {
                            this.goOrder(response.data.order_id)
                        }
                    }
                })
            }).catch(() => { })
        },
        goOrder(orderId) {
            if (!orderId) {
                return
            }

            window.location.href = `/restaurant/v2/orders/${orderId}`
        },
        formatOccupiedTime(minutes) {
            const value = Number(minutes || 0)

            if (value < 60) {
                return `${value.toString().padStart(2, '0')} min`
            }

            const hours = Math.floor(value / 60)
            const mins = value % 60

            return `${hours}h ${mins.toString().padStart(2, '0')}m`
        },

        getDefaultReservationDateTime() {
            return moment().add(1, 'hour').format('YYYY-MM-DD HH:mm:ss')
        },

        reserveTable(table) {
            this.reserveTableSelected = table
            this.cleanReserveForm(false)
            this.reserveForm.reserved_at = this.getDefaultReservationDateTime()
            this.showReserveDialog = true
            this.loadReserveCustomers()
        },

        releaseTable(table) {
            this.$confirm(`¿Liberar la reserva de la mesa ${table.name}?`, 'Liberar mesa', {
                confirmButtonText: 'Liberar',
                cancelButtonText: 'Cancelar',
                type: 'warning',
            }).then(() => {
                this.$http.post(`/${this.resource}/release`, {
                    restaurant_dining_table_id: table.id,
                }).then(response => {
                    if (response.data.success) {
                        this.$message.success(response.data.message)
                        this.getRecords()
                    } else {
                        this.$message.warning(response.data.message)

                        if (response.data.order_id) {
                            this.goOrder(response.data.order_id)
                        }
                    }
                })
            }).catch(() => { })
        },

        cleanReserveForm(cleanTable = true) {
            if (cleanTable) {
                this.reserveTableSelected = null
            }

            this.reserveForm = {
                customer_id: null,
                customer_name: null,
                customer_number: null,
                reserved_observation: null,
                reserved_at: this.getDefaultReservationDateTime(),
            }

            this.reserveCustomers = []
            this.input_person = {}

            this.editingReservation = false
        },

        searchRemoteCustomers(input) {
            this.loadingSearchCustomer = true

            this.$http.get(`/${this.resource}/search/customers`, {
                params: { input }
            }).then(response => {
                this.reserveCustomers = response.data.customers || []
                this.input_person.number = this.reserveCustomers.length === 0 ? input : null
            }).finally(() => {
                this.loadingSearchCustomer = false
            })
        },

        changeReserveCustomer(id) {
            const customer = this.reserveCustomers.find(row => row.id === id)

            if (customer) {
                this.reserveForm.customer_name = customer.name
                this.reserveForm.customer_number = customer.number
            }
        },

        clickCreateCustomer() {
            this.showDialogNewPerson = true
        },

        successAddPerson(person) {
            const customer = {
                id: person.id,
                name: person.name,
                number: person.number,
                description: `${person.number} - ${person.name}`,
            }

            this.reserveCustomers = [customer]
            this.reserveForm.customer_id = customer.id
            this.reserveForm.customer_name = customer.name
            this.reserveForm.customer_number = customer.number
            this.showDialogNewPerson = false
        },

        submitReserveTable() {
            if (this.editingReservation) {
                this.requestEditReservationCode()
                return
            }

            this.saveReservation()
        },

        loadReserveCustomers() {
            this.loadingSearchCustomer = true

            this.$http.get(`/${this.resource}/search/customers`)
                .then(response => {
                    this.reserveCustomers = response.data.customers || []
                })
                .finally(() => {
                    this.loadingSearchCustomer = false
                })
        },

        visibleReserveCustomers(visible) {
            if (visible && this.reserveCustomers.length === 0) {
                this.loadReserveCustomers()
            }
        },

        showReservationDetail(table) {
            this.reservationDetail = table
            this.showReservationDetailDialog = true
        },

        openReservedFromDetail() {
            if (!this.reservationDetail) return

            this.showReservationDetailDialog = false
            this.openTable(this.reservationDetail)
        },

        formatReservationDate(value) {
            if (!value) return '-'

            return moment(value).format('DD/MM/YYYY HH:mm')
        },

        openReservationsDialog() {
            this.showReservationsDialog = true
            this.loadReservations()
        },

        loadReservations() {
            this.loadingReservations = true

            this.$http.get(`/${this.resource}/reservations`)
                .then(response => {
                    this.reservations = response.data.data || []
                })
                .finally(() => {
                    this.loadingReservations = false
                })
        },
        editReservation(row) {
            this.editingReservation = true
            this.reserveTableSelected = {
                id: row.id,
                name: `${row.room_name} - ${row.table_name}`,
            }

            this.reserveCustomers = [{
                id: row.reserved_customer_id,
                name: row.reserved_customer_name,
                number: row.reserved_customer_number,
                description: `${row.reserved_customer_number || '-'} - ${row.reserved_customer_name}`,
            }]

            this.reserveForm = {
                customer_id: row.reserved_customer_id,
                customer_name: row.reserved_customer_name,
                customer_number: row.reserved_customer_number,
                reserved_at: row.reserved_at,
                reserved_observation: row.reserved_observation,
            }

            this.showReservationsDialog = false
            this.showReserveDialog = true
        },

        requestReleaseReservation(row) {
            this.reservationCodeForm = {
                action: 'release',
                code: null,
                row: row,
            }

            this.showReservationCodeDialog = true
        },

        closeReservationCodeDialog() {
            this.showReservationCodeDialog = false
            this.reservationCodeForm = {
                action: null,
                code: null,
                row: null,
            }
        },

        confirmReservationCode() {
            if (!this.reservationCodeForm.code) {
                this.$message.warning('Ingrese el código de autorización')
                return
            }

            if (this.reservationCodeForm.action === 'release') {
                this.releaseReservationFromList()
            }

            if (this.reservationCodeForm.action === 'edit') {
                this.submitReserveTableWithCode()
            }
        },

        releaseReservationFromList() {
            if (!this.reservationCodeForm.row) return

            this.$http.post(`/${this.resource}/release`, {
                restaurant_dining_table_id: this.reservationCodeForm.row.id,
                authorization_code: this.reservationCodeForm.code,
            }).then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.closeReservationCodeDialog()
                    this.loadReservations()
                    this.getRecords()
                } else {
                    this.$message.warning(response.data.message)
                }
            })
        },

        requestEditReservationCode() {
            this.reservationCodeForm = {
                action: 'edit',
                code: null,
                row: null,
            }

            this.showReservationCodeDialog = true
        },

        submitReserveTableWithCode() {
            this.saveReservation(this.reservationCodeForm.code)
        },

        saveReservation(authorizationCode = null) {
            if (!this.reserveTableSelected) return

            if (!this.reserveForm.customer_id && !this.reserveForm.customer_name) {
                this.$message.warning('Selecciona o escribe el cliente de la reserva.')
                return
            }

            this.loadingReserve = true

            const payload = {
                restaurant_dining_table_id: this.reserveTableSelected.id,
                customer_id: this.reserveForm.customer_id,
                customer_name: this.reserveForm.customer_name,
                customer_number: this.reserveForm.customer_number,
                reserved_at: this.reserveForm.reserved_at,
                reserved_observation: this.reserveForm.reserved_observation,
                authorization_code: authorizationCode,
            }

            const request = this.editingReservation
                ? this.$http.put(`/${this.resource}/reservation`, payload)
                : this.$http.post(`/${this.resource}/reserve`, payload)

            request.then(response => {
                if (response.data.success) {
                    this.$message.success(response.data.message)
                    this.showReserveDialog = false
                    this.closeReservationCodeDialog()
                    this.editingReservation = false
                    this.getRecords()
                } else {
                    this.$message.warning(response.data.message)

                    if (response.data.order_id) {
                        this.goOrder(response.data.order_id)
                    }
                }
            }).finally(() => {
                this.loadingReserve = false
            })
        },
        formatReservationHour(value) {
            if (!value) return 'Reservada'

            return moment(value).format('HH:mm')
        },
        isReservationSoon(table) {
            if (!table.reserved_at) return false

            const minutes = moment(table.reserved_at).diff(moment(), 'minutes')

            return minutes >= 0 && minutes <= 60
        },

        openReservationFromList(row) {
            this.$confirm(`¿Abrir la mesa ${row.room_name} - ${row.table_name}?`, 'Abrir reserva', {
                confirmButtonText: 'Abrir',
                cancelButtonText: 'Cancelar',
                type: 'info',
            }).then(() => {
                this.$http.post(`/${this.resource}/open`, {
                    restaurant_dining_table_id: row.id,
                    people_quantity: row.capacity || 1,
                }).then(response => {
                    if (response.data.success) {
                        this.$message.success(response.data.message)
                        this.showReservationsDialog = false
                        this.goOrder(response.data.order_id)
                    } else {
                        this.$message.warning(response.data.message)

                        if (response.data.order_id) {
                            this.goOrder(response.data.order_id)
                        }
                    }
                })
            }).catch(() => { })
        },
    }
}
</script>

<style scoped>
.restaurant-map-card {
    border-radius: 8px;
}

.restaurant-map-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 10px;
}

.restaurant-map-header h4 {
    margin: 0;
    color: #5b6676;
    font-size: 20px;
    font-weight: 500;
}

.restaurant-room-tabs>>>.el-tabs__header {
    margin-bottom: 14px;
}

.restaurant-room-tabs>>>.el-tabs__item {
    font-weight: 600;
    color: #1f2937;
}

.restaurant-room-tabs>>>.el-tabs__item.is-active {
    color: #00a99d;
}

.restaurant-room-tabs>>>.el-tabs__active-bar {
    background-color: #00a99d;
}

.restaurant-room-panel {
    border: 1px solid #e5e9f2;
    border-radius: 8px;
    background: #fff;
    min-height: 520px;
}

.restaurant-room-title {
    min-height: 54px;
    border-bottom: 1px solid #edf0f5;
    padding: 14px 18px;
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
}

.restaurant-room-stats {
    display: flex;
    align-items: center;
    gap: 14px;
    color: #4b5563;
    font-size: 12px;
    flex-wrap: wrap;
}

.restaurant-room-title strong {
    color: #1f2937;
    font-size: 16px;
    font-weight: 800;
    text-transform: uppercase;
}

.restaurant-room-title i {
    color: #5b6676;
    margin-right: 8px;
}

.restaurant-room-stats {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #4b5563;
    font-size: 12px;
    flex-wrap: wrap;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.dot.green {
    background: #67c23a;
}

.dot.red {
    background: #f56c6c;
}

.dot.yellow {
    background: #e6a23c;
}

.restaurant-table-section {
    padding: 16px 22px 6px;
}

.restaurant-table-section h5 {
    margin: 0 0 14px;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.section-free {
    color: #22a63b;
}

.section-occupied {
    color: #e3342f;
}

.section-reserved {
    color: #f59e0b;
}

.restaurant-table-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(118px, 118px));
    gap: 22px 34px;
}

.restaurant-table-tile {
    min-height: 160px;
    border-radius: 8px;
    padding: 14px 10px;
    text-align: center;
    cursor: pointer;
    position: relative;
    border: 1px solid #dfeee0;
    transition: all .15s ease;
}

.restaurant-table-tile:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
}

.restaurant-table-tile.available {
    background: #f4fff4;
    border-color: #cceccc;
}

.restaurant-table-tile.occupied {
    background: #fff3f3;
    border-color: #ffd5d5;
}

.restaurant-table-tile.reserved {
    background: #fff8e8;
    border-color: #ffe0a8;
    cursor: default;
}

.restaurant-table-tile.ready-to-pay {
    background: #eef6ff;
    border-color: #5c9ded;
}

.ready-to-pay .restaurant-table-number {
    background: #d8eaff;
    color: #0b1f3a;
}

.ready-to-pay .restaurant-table-icon {
    color: #5c9ded;
}

.ready-to-pay .ready-label {
    background: #d8eaff;
    color: #2f6fbf;
}

.restaurant-table-number {
    width: 46px;
    height: 46px;
    margin: 0 auto 7px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #111827;
    font-size: 22px;
    font-weight: 800;
}

.available .restaurant-table-number {
    background: #ddf4d7;
}

.occupied .restaurant-table-number {
    background: #ffdada;
}

.reserved .restaurant-table-number {
    background: #ffefc7;
}

.restaurant-table-capacity {
    color: #1f2937;
    font-size: 12px;
    font-weight: 600;
}

.restaurant-table-time {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 8px;
    padding: 4px 8px;
    border-radius: 6px;
    background: #ffe3e3;
    color: #ef4444;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
}

.restaurant-table-order {
    margin-top: 6px;
    color: #1f2937;
    font-size: 11px;
    font-weight: 700;
}

.reserved-label {
    background: #ffe8b6;
    color: #f59e0b;
}

.restaurant-table-icon {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
}

.available .restaurant-table-icon {
    color: #4caf50;
}

.occupied .restaurant-table-icon {
    color: #ef4444;
}

.reserved .restaurant-table-icon {
    color: #f59e0b;
}

@media (max-width: 768px) {
    .restaurant-room-title {
        align-items: flex-start;
        flex-direction: column;
    }

    .restaurant-table-grid {
        grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));
        gap: 14px;
    }
}

.restaurant-color-legend {
    display: flex;
    align-items: center;
    gap: 7px;
    margin-left: auto;
}

.legend-square {
    width: 23px;
    height: 23px;
    border-radius: 5px;
    display: inline-block;
    cursor: help;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
}

.legend-square.free {
    background: #67d98b;
}

.legend-square.ready {
    background: #5c9ded;
}

.legend-square.occupied {
    background: #ef4444;
}

.legend-square.reserved {
    background: #f5a623;
}

.stat-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
}

.stat-square {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
}

.stat-square.free {
    background: #67d98b;
}

.stat-square.occupied {
    background: #ef4444;
}

.stat-square.reserved {
    background: #f5a623;
}

.stat-square.ready {
    background: #5c9ded;
}

.table-mini-action {
    margin-top: 8px;
    border: 0;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
}

.table-mini-action.reserve {
    background: #fff1cc;
    color: #d88900;
}

.table-reserved-actions {
    display: flex;
    justify-content: center;
    gap: 5px;
    margin-top: 8px;
}

.table-reserved-actions button {
    border: 0;
    border-radius: 6px;
    padding: 4px 7px;
    font-size: 11px;
    font-weight: 800;
    background: #00a99d;
    color: #fff;
    cursor: pointer;
}

.table-reserved-actions button.release {
    background: #ef4444;
}

.restaurant-reserve-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
    margin-bottom: 16px;
    border: 1px solid #e5e9f2;
    border-radius: 8px;
    background: #f8fafc;
}

.restaurant-reserve-header span,
.restaurant-reserve-header strong {
    display: block;
}

.restaurant-reserve-header span {
    color: #6b7280;
    font-size: 12px;
}

.restaurant-reserve-header strong {
    color: #1f2937;
    font-size: 15px;
}

.restaurant-reserved-customer {
    margin-top: 6px;
    font-size: 11px;
    line-height: 1.2;
    color: #9a6a00;
    font-weight: 700;
}

.restaurant-reserved-customer small {
    display: block;
    font-size: 10px;
    color: #b98500;
}

.restaurant-table-tile.reserved {
    cursor: pointer;
}

.restaurant-reservation-detail {
    border: 1px solid #e9edf2;
    border-radius: 8px;
    overflow: hidden;
}

.reservation-detail-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 14px;
    border-bottom: 1px solid #eef1f5;
}

.reservation-detail-row:last-child {
    border-bottom: 0;
}

.reservation-detail-row span {
    color: #6b7280;
    font-size: 13px;
}

.reservation-detail-row strong {
    color: #1f2d3d;
    font-size: 14px;
    text-align: right;
}

.restaurant-reservation-list-row {
    padding: 12px 0;
    border-bottom: 1px solid #eef2f6;
}

.restaurant-reservation-list-row strong,
.restaurant-reservation-list-row span,
.restaurant-reservation-list-row small {
    display: block;
}

.restaurant-reservation-list-row strong {
    color: #1f2937;
    font-size: 15px;
}

.restaurant-reservation-list-row span {
    color: #4b5563;
    margin-top: 3px;
}

.restaurant-reservation-list-row small {
    color: #8b98a8;
    margin-top: 3px;
}

.restaurant-reservation-list-row p {
    margin: 8px 0 0;
    padding: 8px 10px;
    border-radius: 6px;
    background: #fff8e6;
    color: #9a6a00;
    font-weight: 700;
}

.restaurant-reservation-list-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.restaurant-reservation-list-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.restaurant-reserved-customer {
    max-width: 96px;
    margin: 7px auto 0;
    color: #9a6a00;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.15;
    text-align: center;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.restaurant-reserved-customer small {
    display: block;
    margin-top: 2px;
    color: #b98500;
    font-size: 9px;
    font-weight: 700;
}

.restaurant-reservation-filters {
    display: grid;
    grid-template-columns: 170px 1fr 1fr;
    gap: 10px;
    margin-bottom: 14px;
}

.restaurant-table-tile.reservation-soon {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, .18);
}

.restaurant-reservation-soon-badge {
    display: inline-block;
    margin-top: 5px;
    padding: 3px 7px;
    border-radius: 10px;
    background: #f59e0b;
    color: #fff;
    font-size: 10px;
    font-weight: 900;
}

@media (max-width: 768px) {
    .restaurant-reservation-filters {
        grid-template-columns: 1fr;
    }
}
</style>


// WEBPACK FOOTER //
// modules/Restaurant/Resources/assets/js/views/v2/tables/index.vue